import dayjs, { type Dayjs } from 'dayjs'
import { onMounted, onUnmounted, shallowRef } from 'vue'
import utc from 'dayjs/plugin/utc'
import isoWeek from 'dayjs/plugin/isoWeek'
import type { Settings, Shift } from '@/types'
import { formatOptionalDecimalHoursForSchedule } from '@/utils/duration'

dayjs.extend(utc)
dayjs.extend(isoWeek)

export type ScheduleView = 'month' | 'week'

export const DATE_FORMAT = 'YYYY-MM-DD'
export const SCHEDULE_VIEW_STORAGE_KEY = 'main-front:schedule-view'

export const isScheduleView = (value: unknown): value is ScheduleView => value === 'month' || value === 'week'

// localStorage может быть недоступен (private mode, политика браузера) — вид графика не должен ломать экран.
export function readStoredScheduleView(): ScheduleView {
    try {
        const stored = localStorage.getItem(SCHEDULE_VIEW_STORAGE_KEY)
        return isScheduleView(stored) ? stored : 'month'
    } catch {
        return 'month'
    }
}

export function writeStoredScheduleView(view: ScheduleView) {
    try {
        localStorage.setItem(SCHEDULE_VIEW_STORAGE_KEY, view)
    } catch {
        // Предпочтение не сохранится, но текущий выбор продолжит работать.
    }
}

export const weekStartOf = (date: Dayjs) => date.startOf('isoWeek').startOf('day')

export const weekDates = (weekStart: Dayjs) => Array.from({ length: 7 }, (_, index) => weekStart.add(index, 'day').format(DATE_FORMAT))

// Месяц → Неделя: текущий месяц открывает текущую неделю, другой — неделю с первым числом месяца.
export function weekStartForMonth(monthDate: Dayjs, today = dayjs()) {
    if (monthDate.isSame(today, 'month')) return weekStartOf(today)
    return weekStartOf(monthDate.startOf('month'))
}

export function formatWeekPeriod(weekStart: Dayjs) {
    const weekEnd = weekStart.add(6, 'day')
    if (weekStart.year() !== weekEnd.year()) {
        return `${weekStart.format('DD.MM.YY')} – ${weekEnd.format('DD.MM.YY')}`
    }
    if (weekStart.month() !== weekEnd.month()) {
        return `${weekStart.format('D MMM')} – ${weekEnd.format('D MMM')}`
    }
    return `${weekStart.format('D')}–${weekEnd.format('D MMMM')}`
}

export const formatWeekPeriodFull = (weekStart: Dayjs) => `${weekStart.format('D MMMM YYYY')} – ${weekStart.add(6, 'day').format('D MMMM YYYY')}`

// Плановые часы смены: duration (с учётом обеда), иначе длительность плана — прежний расчёт графика.
export function getShiftPlanHours(shift: Partial<Shift> | null | undefined) {
    const duration = Number(shift?.duration)
    if (shift?.duration !== null && shift?.duration !== undefined && shift?.duration !== '' && Number.isFinite(duration) && duration >= 0) {
        return duration
    }

    if (!shift?.plan_work_start_at || !shift?.plan_work_end_at) return 0

    const start = dayjs(shift.plan_work_start_at)
    let end = dayjs(shift.plan_work_end_at)

    if (!start.isValid() || !end.isValid()) return 0

    if (end.isSame(start) || end.isBefore(start)) {
        end = end.add(1, 'day')
    }

    return end.diff(start, 'minute') / 60
}

// Время смен график показывает через dayjs.utc(...).format('HH:mm'), то есть как настенное время.
// Для сравнения с «сейчас» берём текущее настенное время в той же шкале.
export const wallClockNow = () => dayjs.utc(dayjs().format('YYYY-MM-DDTHH:mm:ss'))

// Один интервал на все ячейки недели: работает, пока смонтирована хотя бы одна ячейка.
const sharedNow = shallowRef(wallClockNow())
let nowSubscribers = 0
let nowTimer: ReturnType<typeof setInterval> | undefined

export function useWallClockNow() {
    onMounted(() => {
        sharedNow.value = wallClockNow()
        if (nowSubscribers++ === 0) nowTimer = setInterval(() => { sharedNow.value = wallClockNow() }, 60_000)
    })
    onUnmounted(() => {
        if (--nowSubscribers === 0) {
            clearInterval(nowTimer)
            nowTimer = undefined
        }
    })
    return sharedNow
}

export const shiftTime = (value: string | null | undefined) => value ? dayjs.utc(value) : null

// Рассчитанные часы смены из private POST /api/timesheets/ (timesheet.days[DD].shifts[]).
// fact_seconds — пересечение плана и факта, округлённое, за вычетом обеда (api-endpoints.json ::
// main.api.timesheets_create). Время смены сохраняется, чтобы не показать значение, рассчитанное до её изменения.
export interface TimesheetShiftHours {
    seconds: number
    plan_start: string | null
    plan_end: string | null
    fact_start: string | null
    fact_end: string | null
}

export interface TimesheetShiftPayload {
    id?: string
    fact_seconds?: number
    plan_start?: string | null
    plan_end?: string | null
    fact_start?: string | null
    fact_end?: string | null
}

export interface TimesheetResponseItem {
    timesheet?: { days?: Record<string, { shifts?: TimesheetShiftPayload[] }> }
}

export function collectTimesheetShiftHours(items: TimesheetResponseItem[], target: Map<string, TimesheetShiftHours>) {
    for (const item of items) {
        for (const day of Object.values(item.timesheet?.days ?? {})) {
            for (const shift of day.shifts ?? []) {
                if (!shift.id || typeof shift.fact_seconds !== 'number') continue
                target.set(shift.id, {
                    seconds: shift.fact_seconds,
                    plan_start: shift.plan_start ?? null,
                    plan_end: shift.plan_end ?? null,
                    fact_start: shift.fact_start ?? null,
                    fact_end: shift.fact_end ?? null,
                })
            }
        }
    }
}

// Одинаковый момент или одинаковое настенное время (в табеле время может прийти без смещения).
function isSameShiftTime(timesheetValue: string | null, shiftValue: string | null | undefined) {
    if (!timesheetValue || !shiftValue) return !timesheetValue && !shiftValue
    if (dayjs.utc(timesheetValue).valueOf() === dayjs.utc(shiftValue).valueOf()) return true
    return timesheetValue.slice(0, 16) === shiftValue.slice(0, 16)
}

// Отработанные часы завершённой смены. null — значения нет (табель не загружен, смены в нём нет
// или он рассчитан до изменения смены); это не ноль.
export function getTimesheetShiftHours(shift: Partial<Shift>, entry: TimesheetShiftHours | undefined) {
    if (!entry) return null
    const isActual = isSameShiftTime(entry.plan_start, shift.plan_work_start_at)
        && isSameShiftTime(entry.plan_end, shift.plan_work_end_at)
        && isSameShiftTime(entry.fact_start, shift.fact_work_start_at)
        && isSameShiftTime(entry.fact_end, shift.fact_work_end_at)
    return isActual ? entry.seconds / 3600 : null
}

const VIOLATION_STATUSES = ['moderate', 'warning']

export const isViolationStatus = (status: string | null | undefined) => Boolean(status && VIOLATION_STATUSES.includes(status))

// Состояние одной смены для ячеек недели и месяца: план/факт, «идёт», «Нет отметок», нарушения и
// готовые часы табеля. now — общий минутный тикер, hoursEntry — значение табеля по ID смены.
export function describeShiftTiming(shift: Shift, now: Dayjs, hoursEntry: TimesheetShiftHours | undefined) {
    const planStart = shiftTime(shift.plan_work_start_at)
    const planEnd = shiftTime(shift.plan_work_end_at)
    const factStart = shiftTime(shift.fact_work_start_at)
    const factEnd = shiftTime(shift.fact_work_end_at)
    const hasFact = Boolean(factStart || factEnd)
    const isInProgress = Boolean(factStart && !factEnd && planEnd && now.isBefore(planEnd))
    const isUpcoming = !hasFact && Boolean(planStart && now.isBefore(planStart))
    const isNoMarks = !hasFact && Boolean(planEnd && !now.isBefore(planEnd))
    const startViolated = !isNoMarks && isViolationStatus(shift.violation?.start_status)
    const endViolated = !isNoMarks && !isInProgress && isViolationStatus(shift.violation?.end_status)
    // Отработанное — готовое значение табеля по ID смены; пока смена идёт, число не показываем.
    // «Нет отметок» — состояние ячейки с «0:00» по макету. Отсутствие значения в табеле — не ноль.
    const timesheetHours = shift.id ? getTimesheetShiftHours(shift, hoursEntry) : null
    const factHoursValue = isNoMarks ? 0 : (isInProgress || isUpcoming ? null : timesheetHours)
    const planDate = planStart?.format(DATE_FORMAT)

    let factStartText = ''
    let factEndText = ''
    if (!isUpcoming) {
        factStartText = factStart ? factStart.format('HH:mm') : '—'
        factEndText = factEnd ? factEnd.format('HH:mm') : (isInProgress ? '…' : '—')
    }

    return {
        planStartAt: planStart,
        planEndAt: planEnd,
        planStart: planStart ? planStart.format('HH:mm') : '',
        planEnd: planEnd ? planEnd.format('HH:mm') : '',
        planNextDay: Boolean(planEnd && planDate && planEnd.format(DATE_FORMAT) > planDate),
        factEndAt: factEnd,
        // Есть отметка начала без отметки окончания — независимо от планового окончания (в отличие от isInProgress).
        isFactOpen: Boolean(factStart && !factEnd),
        factStart: factStartText,
        factEnd: factEndText,
        factNextDay: Boolean(factEnd && planDate && factEnd.format(DATE_FORMAT) > planDate),
        factHoursValue,
        planHoursValue: getShiftPlanHours(shift),
        isUpcoming,
        isInProgress,
        isNoMarks,
        startViolated,
        endViolated,
        violationMessages: [
            startViolated ? shift.violation?.start_msg : undefined,
            endViolated ? shift.violation?.end_msg : undefined,
        ].filter((message): message is string => Boolean(message)),
        violationCount: Number(startViolated) + Number(endViolated),
        hasGeneralViolation: !isNoMarks && isViolationStatus(shift.violation?.general_status),
    }
}

export type ShiftTiming = ReturnType<typeof describeShiftTiming>

// Заливка кадрового состояния: DayType.color из справочника, пустой/невалидный — --color-day-state-bg.
export function dayStateColor(color: string | null | undefined) {
    const value = color?.trim()
    return value && CSS.supports('color', value) ? value : 'var(--color-day-state-bg)'
}

// RD-185: продолжительность кадрового состояния в ячейках/подсказке графика — только при
// DISPLAY_SHIFT_TIME_IN_CELL: true из настроек пользователя (/me, authStore.settings); нет ключа — не показывается.
export const isDayStateDurationShown = (settings: Partial<Settings> | null | undefined) => settings?.DISPLAY_SHIFT_TIME_IN_CELL === true

// Готовое shift.duration КС (часы, decimal string) → Ч:ММ: "7.50" → 7:30, "0.00" → 0:00; null/нет поля → «—».
// Не вычисляется из отметок/плана и не складывается с часами рабочих смен.
export const formatDayStateDuration = (shift: Partial<Shift> | null | undefined) => formatOptionalDecimalHoursForSchedule(shift?.duration) ?? '—'

// Смена в месячной ячейке и её подсказке (RD-138).
export interface MonthShiftView extends ShiftTiming {
    key: string
    isExtra: boolean
    positionName: string
    planEndDate: string
    factEndDate: string
    hoursText: string
}

export function pluralViolations(count: number) {
    const mod10 = count % 10
    const mod100 = count % 100
    if (mod10 === 1 && mod100 !== 11) return `${count} нарушение`
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${count} нарушения`
    return `${count} нарушений`
}

const TOOLTIP_VIEWPORT_MARGIN = 8
const TOOLTIP_ANCHOR_GAP = 4

// Позиция fixed-подсказки ячейки (месяц графика и табеля): под ячейкой, при нехватке места — над ней;
// по горизонтали не выходит за правый край viewport.
export function placeCellTooltip(element: HTMLElement, anchor: HTMLElement) {
    const rect = anchor.getBoundingClientRect()
    const viewportWidth = document.documentElement.clientWidth
    const viewportHeight = document.documentElement.clientHeight
    const width = element.offsetWidth
    const height = element.offsetHeight

    let left = rect.left
    if (left + width > viewportWidth - TOOLTIP_VIEWPORT_MARGIN) left = rect.right - width
    left = Math.min(Math.max(TOOLTIP_VIEWPORT_MARGIN, left), viewportWidth - TOOLTIP_VIEWPORT_MARGIN - width)

    let top = rect.bottom + TOOLTIP_ANCHOR_GAP
    if (top + height > viewportHeight - TOOLTIP_VIEWPORT_MARGIN) top = rect.top - TOOLTIP_ANCHOR_GAP - height
    top = Math.min(Math.max(TOOLTIP_VIEWPORT_MARGIN, top), viewportHeight - TOOLTIP_VIEWPORT_MARGIN - height)

    return { left, top }
}
